/**
 * 광고 fallback — 등록된 프로모션이 없을 때 자동으로 노출하는 카드.
 *
 * 영구지침: docs/develop/frontend/home-page-portal-spec.md §4.4
 *
 * 슬롯 성격에 따라 풀을 두 개로 분리 운영 (v5.1.1, 2026-05-04):
 * - variant='grid' → 자체 서비스 CTA 풀 (4종, 사용자 가치 액션 유도)
 * - variant='hero' | 'card' | 'inline' → 광고 모집 풀 (1종, 영업 리드)
 *
 * 사용:
 *   <PromotionSlot
 *     slotKey="main_card_slider"
 *     layout="slider"
 *     fallback={<MockPromotionFallback variant="card" count={3} />}
 *   />
 */
import { Link } from 'react-router-dom'
import { Sparkles, ClipboardCheck, Megaphone } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

interface MockItem {
  title: string
  desc: string
  to: string
  Icon: LucideIcon
  gradient: string
}

const INTERNAL_CTA_POOL: MockItem[] = [
  {
    title: '요양등급 자가진단',
    desc: '8문항 무료 등급 추정',
    to: '/guide/self-assessment',
    Icon: ClipboardCheck,
    gradient: 'from-emerald-500 to-teal-500',
  },
  {
    title: 'AI 맞춤 시설 추천',
    desc: '3분 자가진단 → 맞춤 5곳',
    to: '/recommend',
    Icon: Sparkles,
    gradient: 'from-purple-500 to-pink-500',
  },
  {
    title: '비용 시뮬레이터',
    desc: '월 부담금 1분 만에 계산',
    to: '/cost-simulator',
    Icon: Sparkles,
    gradient: 'from-cyan-500 to-sky-600',
  },
  {
    title: '돌봄일지 무료 시작',
    desc: '시설과 매일 양방향 기록',
    to: '/mypage/care-log',
    Icon: ClipboardCheck,
    gradient: 'from-rose-500 to-pink-500',
  },
]

const AD_RECRUIT_ITEM: MockItem = {
  title: '이 자리에 광고하세요',
  desc: '1:1 광고 문의 환영',
  to: '/mypage/inquiry/new?category=AD_INQUIRY',
  Icon: Megaphone,
  gradient: 'from-slate-600 to-slate-800',
}

interface Props {
  variant: 'card' | 'grid' | 'hero' | 'inline'
  count?: number
}

export default function MockPromotionFallback({ variant, count = 3 }: Props) {
  if (variant === 'grid') {
    const day = new Date().getDate()
    const offset = day % INTERNAL_CTA_POOL.length
    const items = Array.from(
      { length: count },
      (_, i) => INTERNAL_CTA_POOL[(offset + i) % INTERNAL_CTA_POOL.length] as MockItem,
    )
    return (
      <div className="grid grid-cols-2 gap-2">
        {items.map((it, i) => (
          <Link
            key={i}
            to={it.to}
            className={`group relative aspect-square overflow-hidden rounded-2xl bg-gradient-to-br ${it.gradient} p-3 text-white hover:opacity-95`}
          >
            <span className="absolute right-2 top-2 rounded bg-black/20 px-1.5 py-0.5 text-[9px] font-bold backdrop-blur-sm">
              AD
            </span>
            <it.Icon size={28} className="text-white/85" />
            <p className="mt-2 line-clamp-2 text-xs font-bold leading-tight">
              {it.title}
            </p>
          </Link>
        ))}
      </div>
    )
  }

  const it = AD_RECRUIT_ITEM

  if (variant === 'hero') {
    return (
      <Link
        to={it.to}
        className={`flex items-center justify-between rounded-2xl bg-gradient-to-r ${it.gradient} px-8 py-5 text-white hover:opacity-95`}
      >
        <div>
          <p className="text-xs font-semibold text-white/80">AD</p>
          <p className="mt-0.5 text-lg font-bold">{it.title}</p>
          <p className="mt-0.5 text-sm text-white/80">{it.desc}</p>
        </div>
        <it.Icon size={36} className="text-white/80" />
      </Link>
    )
  }

  if (variant === 'inline') {
    return (
      <Link
        to={it.to}
        className={`flex items-center justify-between rounded-2xl bg-gradient-to-r ${it.gradient} px-5 py-4 text-white hover:opacity-95`}
      >
        <div className="min-w-0">
          <p className="truncate text-sm font-bold">{it.title}</p>
          <p className="truncate text-xs text-white/80">{it.desc}</p>
        </div>
        <it.Icon size={24} className="ml-2 flex-shrink-0 text-white/85" />
      </Link>
    )
  }

  // variant === 'card'
  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      <Link
        to={it.to}
        className={`flex h-32 min-w-[280px] max-w-[320px] flex-shrink-0 flex-col justify-between rounded-2xl bg-gradient-to-br ${it.gradient} p-4 text-white hover:opacity-95`}
      >
        <span className="rounded bg-white/20 px-2 py-0.5 text-[10px] font-bold w-fit">
          AD
        </span>
        <div>
          <p className="text-base font-bold">{it.title}</p>
          <p className="mt-0.5 text-xs text-white/80">{it.desc}</p>
        </div>
      </Link>
    </div>
  )
}
